import Badge from '@mui/icons-material/Badge'
import Close from '@mui/icons-material/Close'
import Delete from '@mui/icons-material/Delete'
import PersonAdd from '@mui/icons-material/PersonAdd'
import Refresh from '@mui/icons-material/Refresh'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  getDataMapFromCommonResponse,
  getImageLink,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getSPDataWithParams, postSPDataWithParams } from 'src/supportPortal/services/services'

/**
 * View/manage drawer for a clinic brand's owners (and brand admins).
 * There is no dedicated ClinicOwner entity on the backend — ownership is
 * ClinicBrand.ownerUserIds — so each row surfaces the backing User record
 * (userId, username, userNumber, email, mobile, verification/account state).
 *
 * Managing (add/remove owner) is CSA/admin-only; the backend enforces the same.
 */
export default function ClinicOwnersDrawer({ open, brand, canManage = false, onClose }) {
  const clinicBrandId = brand?.id
  const [loading, setLoading] = useState(false)
  const [owners, setOwners] = useState([])
  const [admins, setAdmins] = useState([])
  const [errorMsg, setErrorMsg] = useState('')
  const [addIdentifier, setAddIdentifier] = useState('')
  const [saving, setSaving] = useState(false)
  const [pendingRemoveUserId, setPendingRemoveUserId] = useState(null)
  // Effective owner-portal visibility (backend sends resolved booleans, null = enabled).
  const [portalSettings, setPortalSettings] = useState(null)
  const [savingSettings, setSavingSettings] = useState(false)

  const applyResponse = useCallback((resp) => {
    const data = getDataMapFromCommonResponse(resp) || {}
    setOwners(Array.isArray(data.owners) ? data.owners : [])
    setAdmins(Array.isArray(data.admins) ? data.admins : [])
    setPortalSettings(data?.['owner-portal-settings'] || null)
  }, [])

  const load = useCallback(async () => {
    if (!clinicBrandId) return
    setLoading(true)
    setErrorMsg('')
    try {
      const resp = await getSPDataWithParams('/api/secure/cc/support/clinic-brand/owners', {
        clinicBrandId,
      })
      if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        applyResponse(resp)
      } else {
        setOwners([])
        setAdmins([])
        setErrorMsg(
          resp?.response?.data?.message ||
            resp?.data?.message ||
            'Could not load clinic owners. You may not have access to this brand.',
        )
      }
    } catch (e) {
      setErrorMsg('Could not load clinic owners.')
    } finally {
      setLoading(false)
    }
  }, [clinicBrandId, applyResponse])

  useEffect(() => {
    if (open) {
      setAddIdentifier('')
      setPendingRemoveUserId(null)
      load()
    }
  }, [open, load])

  async function updateOwners(action, identifier) {
    setSaving(true)
    try {
      const resp = await postSPDataWithParams(
        '/api/secure/cc/support/clinic-brand/owners/update',
        {},
        { clinicBrandId, action, identifier },
      )
      if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        applyResponse(resp)
        toast.success(action === 'add' ? 'Owner added' : 'Owner removed')
        setAddIdentifier('')
        setPendingRemoveUserId(null)
      } else {
        toast.error(
          resp?.response?.data?.message || resp?.data?.message || 'Could not update owners',
        )
      }
    } finally {
      setSaving(false)
    }
  }

  async function updatePortalSetting(key, value) {
    const nextSettings = { ...portalSettings, [key]: value }
    setSavingSettings(true)
    try {
      const resp = await postSPDataWithParams(
        '/api/secure/cc/support/clinic-brand/owner-portal-settings/update',
        nextSettings,
        { clinicBrandId },
      )
      if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        applyResponse(resp)
        toast.success('Owner portal visibility updated')
      } else {
        toast.error(
          resp?.response?.data?.message || resp?.data?.message || 'Could not update visibility',
        )
      }
    } finally {
      setSavingSettings(false)
    }
  }

  return (
    <Drawer
      anchor="right"
      open={Boolean(open)}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, maxWidth: '100vw' } }}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Badge color="primary" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" noWrap>
            Clinic Owner
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {brand?.title || brand?.brandName || clinicBrandId}
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={load} disabled={loading}>
            <Refresh />
          </IconButton>
        </Tooltip>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </Box>
      <Divider />
      <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : errorMsg ? (
          <Alert severity="error">{errorMsg}</Alert>
        ) : (
          <Stack spacing={2}>
            <Typography variant="subtitle2">
              Owners {owners?.length ? `(${owners.length})` : ''}
            </Typography>
            {!owners?.length && (
              <Alert severity="warning">This clinic brand has no owner attached.</Alert>
            )}
            {owners?.map((owner) => (
              <OwnerCard
                key={owner?.userId}
                user={owner}
                canRemove={canManage && owners?.length > 1}
                removing={saving && pendingRemoveUserId === owner?.userId}
                pendingRemove={pendingRemoveUserId === owner?.userId}
                onRemoveIntent={() => setPendingRemoveUserId(owner?.userId)}
                onRemoveCancel={() => setPendingRemoveUserId(null)}
                onRemoveConfirm={() => updateOwners('remove', owner?.userId)}
              />
            ))}
            {canManage && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Add owner
                </Typography>
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="userId / username / email / mobile"
                    value={addIdentifier}
                    onChange={(e) => setAddIdentifier(e.target.value)}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PersonAdd />}
                    disabled={saving || !addIdentifier.trim()}
                    onClick={() => updateOwners('add', addIdentifier.trim())}
                  >
                    Add
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  The user also gets ROLE_CLINIC_OWNER so the owner portal unlocks for them.
                </Typography>
              </Paper>
            )}
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Owner portal visibility
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Controls the &quot;Business Model &amp; Invoices&quot; surfaces the brand&apos;s owners
                see in the mentor portal. Enforced by the server, not just hidden. Default is off —
                new brands stay hidden until enabled here.
              </Typography>
              <Stack>
                {[
                  { key: 'showBusinessModels', label: 'Business Models' },
                  { key: 'showPayoutReports', label: 'Payout Reports' },
                  { key: 'showPayoutInvoices', label: 'Payout Invoices' },
                ].map(({ key, label }) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Switch
                        size="small"
                        checked={portalSettings?.[key] === true}
                        disabled={!canManage || savingSettings}
                        onChange={(e) => updatePortalSetting(key, e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">{label}</Typography>}
                  />
                ))}
              </Stack>
              {!canManage && (
                <Typography variant="caption" color="text.secondary">
                  Only Clinic Success Admins can change these.
                </Typography>
              )}
            </Paper>
            {admins?.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>
                  Brand Admins ({admins.length})
                </Typography>
                {admins.map((admin) => (
                  <OwnerCard key={admin?.userId} user={admin} canRemove={false} />
                ))}
              </>
            )}
          </Stack>
        )}
      </Box>
    </Drawer>
  )
}

function OwnerCard({
  user,
  canRemove = false,
  removing = false,
  pendingRemove = false,
  onRemoveIntent,
  onRemoveCancel,
  onRemoveConfirm,
}) {
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ')
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Avatar src={user?.profilePicId ? getImageLink(user.profilePicId) : undefined}>
          {(fullName || user?.userName || '?').charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2" noWrap>
              {fullName || user?.userName || user?.userId}
            </Typography>
            {user?.enabled === false && <Chip label="Disabled" size="small" color="error" />}
            {user?.locked && <Chip label="Locked" size="small" color="warning" />}
            {user?.missingUserRecord && (
              <Chip label="User record missing" size="small" color="error" />
            )}
          </Stack>
          <PropertyRow label="User ID" value={user?.userId} />
          <PropertyRow label="Username" value={user?.userName} />
          <PropertyRow label="User Number" value={user?.userNumber} />
          <PropertyRow
            label="Email"
            value={user?.email}
            verified={user?.email ? user?.emailVerified : undefined}
          />
          <PropertyRow
            label="Mobile"
            value={user?.mobile}
            verified={user?.mobile ? user?.mobileVerified : undefined}
          />
          {Array.isArray(user?.authorities) && user.authorities.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
              {user.authorities.map((authority) => (
                <Chip
                  key={authority}
                  label={authority.replace(/^ROLE_/, '')}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Stack>
          )}
        </Box>
        {canRemove &&
          (pendingRemove ? (
            <Stack spacing={0.5}>
              <Button
                size="small"
                color="error"
                variant="contained"
                disabled={removing}
                onClick={onRemoveConfirm}
              >
                {removing ? 'Removing…' : 'Confirm'}
              </Button>
              <Button size="small" disabled={removing} onClick={onRemoveCancel}>
                Cancel
              </Button>
            </Stack>
          ) : (
            <Tooltip title="Remove owner">
              <IconButton size="small" color="error" onClick={onRemoveIntent}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          ))}
      </Stack>
    </Paper>
  )
}

function PropertyRow({ label, value, verified }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 88 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
        {value || '—'}
      </Typography>
      {verified === true && <Chip label="Verified" size="small" color="success" sx={{ height: 16 }} />}
      {verified === false && (
        <Chip label="Unverified" size="small" variant="outlined" sx={{ height: 16 }} />
      )}
    </Stack>
  )
}
